import React, { useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  Activity,
  BarChart3,
  Bot,
  CheckCircle2,
  CircleDollarSign,
  Crosshair,
  FileJson,
  GitBranch,
  Gauge,
  LineChart,
  Medal,
  Radio,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Trophy,
  WalletCards,
} from "lucide-react";
import { defaultWeights, eventSnapshot, matches } from "./data";
import { buildPredictions, buildWatchBrief, formatPercent } from "./forecast";
import { abilityLabels, scorePlayers, type PlayerScore, type RatingMode, type WindowKey } from "./players";
import type { Prediction, WeightKey, Weights } from "./types";
import { worldCupGroups, worldCupMatches, worldCupSource, worldCupStadiums, worldCupTeams, type WorldCupGroup, type WorldCupMatch, type WorldCupStadium, type WorldCupTeam } from "./worldcupData";
import { injectivePlaybookSummary, injectivePlays, type InjectivePlay } from "./injectivePlaybook";
import "./styles.css";

const weightLabels: Record<WeightKey, string> = {
  form: "Form",
  attack: "Attack",
  defense: "Defense",
  pressure: "Pressure",
  travel: "Travel",
};

type Lang = "en" | "zh";
type RefreshState = "idle" | "loading" | "success" | "error";
type OnchainProof = {
  status: "pending" | "live";
  network: { name: string; caip2: string };
  contract?: { address: string; explorer: string };
  anchor?: { transaction: string; explorer: string; proofSha256: string };
};
type X402PaymentProof = {
  status: "settled";
  endpoint: string;
  transaction: { hash: string; explorer: string };
  payment: { amountUsdc: string; payer: string; payee: string };
};
type CctpPaymentProof = {
  status: "success";
  amount: string;
  sourceTransaction: string;
  destinationTransaction: string;
  balanceBefore: string;
  balanceAfter: string;
  explorer: { source: string; destination: string };
};
type WorldCupRuntimeData = {
  teams: WorldCupTeam[];
  groups: WorldCupGroup[];
  matches: WorldCupMatch[];
  stadiums: WorldCupStadium[];
  source: Record<string, string>;
  updatedAt: string;
  mode: "snapshot" | "live-browser";
  error?: string;
};

const copy = {
  en: {
    playbookTitle: "Four technical hooks, four matchday actions",
    playbookBody:
      "x402 sells premium scout intel, CCTP funds the fan pool, MCP Server hands match data to agents, and Agent Skills turn live football context into a repeatable posting workflow.",
    realTitle: "Real World Cup 2026 data is wired in",
    realBody:
      "The data layer uses openfootball/worldcup and rezarahiminia/worldcup2026: 48 teams, 12 groups, 72 group-stage matches, 32 knockout slots, and 16 stadiums.",
    playerTitle: "Live player ratings, form state, and ability deltas",
    playerBody:
      "The player-analysis layer blends match-style events, xG/xA, pressing, defensive actions, running load, and pre-match ability into a product-ready rating model.",
    motionTitle: "Match momentum as a moving data scene",
    motionBody:
      "Pitch routes, live rankings, player portraits, and Injective payment nodes share one animated canvas. It shows who is creating edge, where the ball is moving, and which panel is worth capturing next.",
    langButton: "ZH",
    langLabel: "Language",
    refreshIdle: "Refresh Live Data",
    refreshLoading: "Updating...",
    refreshSuccess: "Live data updated",
    refreshError: "Live API unavailable",
  },
  zh: {
    playbookTitle: "四个技术点不是贴标签，而是观赛动作",
    playbookBody:
      "x402 卖出付费情报，CCTP 完成 USDC 球迷奖池跨链，MCP Server 把数据交给 Agent，Agent Skill 把实时赛事转成可发帖的操作流。",
    realTitle: "接入 2026 世界杯真实赛程数据",
    realBody:
      "数据层来自 openfootball/worldcup 与 rezarahiminia/worldcup2026：48 支球队、12 个小组、72 场小组赛、32 场淘汰赛槽位和 16 座球场。",
    playerTitle: "本场球员评分、实时状态和能力差值",
    playerBody:
      "这是产品演示用的模拟球员分析层：把类比赛事件、xG/xA、压迫、防守动作、跑动负荷和赛前能力值合成评分，不声称是官方实时球员数据。",
    motionTitle: "把比赛走势做成会动的数据场景",
    motionBody:
      "球场线路、实时排名、球员头像和链上支付节点被放在同一个动态画布里。它不是装饰背景，而是把谁在制造优势、球权往哪里走、下一步该截图什么表达出来。",
    langButton: "EN",
    langLabel: "语言",
    refreshIdle: "更新实时数据",
    refreshLoading: "更新中...",
    refreshSuccess: "实时数据已更新",
    refreshError: "实时接口不可用",
  },
} satisfies Record<Lang, Record<string, string>>;

const initialWorldCupRuntimeData: WorldCupRuntimeData = {
  teams: worldCupTeams,
  groups: worldCupGroups,
  matches: worldCupMatches,
  stadiums: worldCupStadiums,
  source: worldCupSource,
  updatedAt: "",
  mode: "snapshot",
};

function assetPath(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  return `${import.meta.env.BASE_URL}${path.replace(/^\//, "")}`;
}

async function fetchJsonWithTimeout(url: string, timeoutMs = 12000): Promise<unknown> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { signal: controller.signal, headers: { accept: "application/json" } });
    if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
    return response.json();
  } finally {
    window.clearTimeout(timer);
  }
}

async function fetchLiveWorldCupData(): Promise<WorldCupRuntimeData> {
  const base = "https://worldcup26.ir/get";
  const [teamPayload, stadiumPayload, gamePayload] = await Promise.all([
    fetchJsonWithTimeout(`${base}/teams`),
    fetchJsonWithTimeout(`${base}/stadiums`),
    fetchJsonWithTimeout(`${base}/games`),
  ]);
  const teams = ((teamPayload as { teams?: unknown[] }).teams ?? (Array.isArray(teamPayload) ? teamPayload : [])).map(normalizeLiveTeam);
  const stadiums = ((stadiumPayload as { stadiums?: unknown[] }).stadiums ?? (Array.isArray(stadiumPayload) ? stadiumPayload : [])).map(normalizeLiveStadium);
  const teamById = new Map(teams.map((team) => [team.id, team]));
  const stadiumById = new Map(stadiums.map((stadium) => [stadium.id, stadium]));
  const matches = ((gamePayload as { games?: unknown[] }).games ?? (Array.isArray(gamePayload) ? gamePayload : [])).map((item) =>
    normalizeLiveMatch(item, teamById, stadiumById),
  );
  if (teams.length < 40 || stadiums.length < 10 || matches.length < 60) {
    throw new Error(`incomplete payload: ${teams.length} teams, ${stadiums.length} stadiums, ${matches.length} matches`);
  }
  const groups = [...new Set(teams.map((team) => team.group).filter(Boolean))]
    .sort()
    .map((group) => ({ group, teams: teams.filter((team) => team.group === group).map((team) => team.fifaCode) }));
  return {
    teams,
    stadiums,
    groups,
    matches,
    source: {
      liveApi: `${base}/*`,
      updateMode: "browser-live-api",
      teams: `${base}/teams`,
      groupMatches: `${base}/games`,
      stadiums: `${base}/stadiums`,
      knockout: "current bundled snapshot for knockout slots",
      context: "browser refresh",
    },
    updatedAt: new Date().toISOString(),
    mode: "live-browser",
  };
}

function normalizeLiveTeam(item: unknown): WorldCupTeam {
  const record = item as Record<string, unknown>;
  return {
    id: Number(record.id),
    name: String(record.name_en ?? record.name ?? "TBD"),
    fifaCode: String(record.fifa_code ?? record.fifaCode ?? "TBD"),
    iso2: String(record.iso2 ?? "TBD"),
    group: String(record.groups ?? record.group ?? ""),
    flag: String(record.flag ?? ""),
  };
}

function normalizeLiveStadium(item: unknown): WorldCupStadium {
  const record = item as Record<string, unknown>;
  return {
    id: Number(record.id),
    name: String(record.name_en ?? record.name ?? record.fifa_name ?? ""),
    city: String(record.city_en ?? record.city ?? ""),
    country: String(record.country_en ?? record.country ?? ""),
    capacity: Number(record.capacity ?? 0),
  };
}

function normalizeLiveMatch(item: unknown, teamById: Map<number, WorldCupTeam>, stadiumById: Map<number, WorldCupStadium>): WorldCupMatch {
  const record = item as Record<string, unknown>;
  const home = teamById.get(Number(record.home_team_id));
  const away = teamById.get(Number(record.away_team_id));
  const stadium = stadiumById.get(Number(record.stadium_id));
  return {
    id: Number(record.id),
    type: String(record.type ?? "group"),
    group: String(record.group ?? ""),
    matchday: Number(record.matchday ?? 0),
    localDate: String(record.local_date ?? record.localDate ?? ""),
    date: String(record.date ?? ""),
    home: home?.fifaCode ?? "TBD",
    away: away?.fifaCode ?? "TBD",
    homeName: home?.name ?? "TBD",
    awayName: away?.name ?? "TBD",
    stadium: stadium?.name ?? "",
    city: stadium?.city ?? "",
    country: stadium?.country ?? "",
    source: "worldcup26.ir browser refresh",
  };
}

function App() {
  const [lang, setLang] = useState<Lang>("en");
  const [scrollProgress, setScrollProgress] = useState(0);
  const [weights, setWeights] = useState<Weights>(defaultWeights);
  const [selectedId, setSelectedId] = useState(matches[0].id);
  const [ratingMode, setRatingMode] = useState<RatingMode>("balanced");
  const [windowKey, setWindowKey] = useState<WindowKey>("live");
  const [selectedPlayerId, setSelectedPlayerId] = useState("arg-10");
  const [paid, setPaid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [worldData, setWorldData] = useState<WorldCupRuntimeData>(initialWorldCupRuntimeData);
  const [refreshState, setRefreshState] = useState<RefreshState>("idle");
  const [refreshMessage, setRefreshMessage] = useState("");
  const [onchainProof, setOnchainProof] = useState<OnchainProof | null>(null);
  const [x402Payment, setX402Payment] = useState<X402PaymentProof | null>(null);
  const [cctpPayment, setCctpPayment] = useState<CctpPaymentProof | null>(null);
  const predictions = useMemo(() => buildPredictions(weights), [weights]);
  const selected = predictions.find((prediction) => prediction.match.id === selectedId) ?? predictions[0];
  const brief = useMemo(() => buildWatchBrief(selected.match.id, weights), [selected.match.id, weights]);
  const playerScores = useMemo(() => scorePlayers(ratingMode, windowKey), [ratingMode, windowKey]);
  const selectedPlayer = playerScores.find((score) => score.player.id === selectedPlayerId) ?? playerScores[0];

  useEffect(() => {
    setSelectedPlayerId(playerScores[0].player.id);
  }, [playerScores]);

  useEffect(() => {
    const handleScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(max > 0 ? Math.min(1, window.scrollY / max) : 0);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    fetch(assetPath("/proofs/cctp-transfer.json"), { headers: { accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : null))
      .then((proof) => setCctpPayment(proof?.status === "success" ? proof : null))
      .catch(() => setCctpPayment(null));
  }, []);

  useEffect(() => {
    fetch(assetPath("/proofs/x402-payment.json"), { headers: { accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : null))
      .then((proof) => setX402Payment(proof))
      .catch(() => setX402Payment(null));
  }, []);

  useEffect(() => {
    fetch(assetPath("/proofs/onchain-proof.json"), { headers: { accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : null))
      .then((proof) => setOnchainProof(proof))
      .catch(() => setOnchainProof(null));
  }, []);

  useEffect(() => {
    const handlePointer = (event: PointerEvent) => {
      document.documentElement.style.setProperty("--pointer-x", `${event.clientX}px`);
      document.documentElement.style.setProperty("--pointer-y", `${event.clientY}px`);
    };
    window.addEventListener("pointermove", handlePointer, { passive: true });
    return () => window.removeEventListener("pointermove", handlePointer);
  }, []);

  function unlockReport() {
    setLoading(true);
    window.setTimeout(() => {
      setPaid(true);
      setLoading(false);
    }, 650);
  }

  async function refreshLiveData() {
    setRefreshState("loading");
    setRefreshMessage(copy[lang].refreshLoading);
    try {
      const next = await fetchLiveWorldCupData();
      setWorldData(next);
      setRefreshState("success");
      setRefreshMessage(`${copy[lang].refreshSuccess}: ${next.teams.length} teams / ${next.matches.length} matches`);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setRefreshState("error");
      setRefreshMessage(`${copy[lang].refreshError}: ${message}`);
      setWorldData((current) => ({ ...current, error: message }));
    }
  }

  return (
    <main>
      <StageCanvas />
      <div className="cursor-orb" aria-hidden="true" />
      <div className="scroll-progress" style={{ transform: `scaleX(${scrollProgress})` }} />
      <LanguageToggle lang={lang} onToggle={() => setLang((current) => (current === "en" ? "zh" : "en"))} />
      <Hero selected={selected} lang={lang} />
      <VisualReel />
      <RealWorldCupDataPanel
        lang={lang}
        data={worldData}
        refreshState={refreshState}
        refreshMessage={refreshMessage}
        onRefresh={refreshLiveData}
      />
      <section className="shell app-grid" aria-label="Cup Signal cockpit">
        <MatchRail predictions={predictions} selectedId={selectedId} onSelect={setSelectedId} />
        <SignalPanel selected={selected} />
        <ControlPanel weights={weights} setWeights={setWeights} />
      </section>
      <PlayerDashboard
        lang={lang}
        scores={playerScores}
        selectedScore={selectedPlayer}
        selectedPlayerId={selectedPlayer.player.id}
        onSelect={setSelectedPlayerId}
        ratingMode={ratingMode}
        onMode={setRatingMode}
        windowKey={windowKey}
        onWindow={setWindowKey}
      />
      <WorldCupMotionPanel lang={lang} scores={playerScores} selected={selected} />
      <InjectivePlaybookPanel
        lang={lang}
        onchainProof={onchainProof}
        x402Payment={x402Payment}
        cctpPayment={cctpPayment}
      />
      <section className="shell lower-grid">
        <InjectivePanel paid={paid} loading={loading} onUnlock={unlockReport} brief={brief} />
        <AgentPanel selected={selected} />
      </section>
      <section className="shell submission-band">
        <div>
          <p className="eyebrow">Submission Pack</p>
          <h2>Built for the Global Cup scoring loop</h2>
          <p>
            README, MCP command, Agent Skill spec, x402 endpoint, CCTP transfer proof, and on-chain receipts are included in the repository.
            The public tweet draft can use this page screenshot plus the GitHub and demo links.
          </p>
        </div>
        <div className="submission-list">
          {eventSnapshot.judgingFocus.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
      </section>
    </main>
  );
}

function StageCanvas() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let frame = 0;
    let raf = 0;
    let width = 0;
    let height = 0;
    const pointer = { x: 0.72, y: 0.28 };
    const nodes = Array.from({ length: 74 }, (_, index) => ({
      seed: index * 17.13,
      radius: 0.7 + (index % 5) * 0.22,
      speed: 0.0025 + (index % 7) * 0.0007,
    }));

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const move = (event: PointerEvent) => {
      pointer.x = event.clientX / Math.max(1, width);
      pointer.y = event.clientY / Math.max(1, height);
    };

    const draw = () => {
      frame += 1;
      ctx.clearRect(0, 0, width, height);
      const gradient = ctx.createRadialGradient(pointer.x * width, pointer.y * height, 0, pointer.x * width, pointer.y * height, width * 0.72);
      gradient.addColorStop(0, "rgba(204, 47, 61, 0.22)");
      gradient.addColorStop(0.28, "rgba(207, 178, 106, 0.08)");
      gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      ctx.strokeStyle = "rgba(255,255,255,0.055)";
      ctx.lineWidth = 1;
      for (let x = (frame * 0.18) % 96; x < width; x += 96) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x - height * 0.24, height);
        ctx.stroke();
      }

      const points = nodes.map((node) => {
        const t = frame * node.speed + node.seed;
        return {
          x: width * (0.5 + Math.sin(t * 1.7) * 0.46) + (pointer.x - 0.5) * 34,
          y: height * (0.5 + Math.cos(t * 1.19) * 0.42) + (pointer.y - 0.5) * 28,
          r: node.radius,
        };
      });

      for (let i = 0; i < points.length; i += 1) {
        const a = points[i];
        for (let j = i + 1; j < points.length; j += 11) {
          const b = points[j];
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          if (dist < 150) {
            ctx.strokeStyle = `rgba(255,255,255,${0.09 - dist / 1900})`;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
        ctx.fillStyle = i % 8 === 0 ? "rgba(207,178,106,0.9)" : "rgba(235,241,238,0.58)";
        ctx.beginPath();
        ctx.arc(a.x, a.y, a.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener("resize", resize);
    window.addEventListener("pointermove", move, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", move);
    };
  }, []);

  return <canvas ref={canvasRef} className="stage-canvas" aria-hidden="true" />;
}

function VisualReel() {
  const rows = [
    [
      "/media/cup-signal-teaser-cover.jpg",
      "/players/generated-web/arg-10-messi-chibi.webp",
      "/players/generated-web/arg-11-di-maria-chibi.webp",
      "/players/generated-web/arg-24-enzo-chibi.webp",
      "/players/generated-web/arg-09-alvarez-chibi.webp",
      "/players/generated-web/arg-20-mac-allister-chibi.webp",
    ],
    [
      "/players/generated-web/arg-23-martinez-chibi.webp",
      "/players/generated-web/arg-07-de-paul-chibi.webp",
      "/players/generated-web/arg-17-garnacho-chibi.webp",
      "/players/generated-web/arg-03-tagliafico-chibi.webp",
      "/players/generated-web/arg-13-romero-chibi.webp",
      "/media/cup-signal-teaser-cover.jpg",
    ],
  ];

  return (
    <section className="visual-reel" aria-label="Cup Signal visual reel">
      {rows.map((row, rowIndex) => (
        <div className={rowIndex === 0 ? "visual-reel-track row-one" : "visual-reel-track row-two"} key={rowIndex}>
          {[...row, ...row, ...row].map((src, index) => (
            <img src={assetPath(src)} alt="" loading="lazy" key={`${src}-${index}`} />
          ))}
        </div>
      ))}
    </section>
  );
}

function MotionPromptStrip() {
  const prompts = [
    "Animated matchday cockpit",
    "x402 unlock moment",
    "CCTP fan-pool rail",
    "MCP agent analyst",
    "FUT-style player card",
    "World Cup data stream",
  ];
  return (
    <section className="motion-prompt-strip" aria-label="Motion design prompt strip">
      <div className="prompt-track">
        {[...prompts, ...prompts].map((prompt, index) => (
          <span key={`${prompt}-${index}`}>{prompt}</span>
        ))}
      </div>
    </section>
  );
}

function StudioIndex({ selected, topPlayer }: { selected: Prediction; topPlayer: PlayerScore }) {
  const rows = [
    {
      key: "01",
      label: "Match Signal",
      value: `${selected.home.code} ${selected.projectedScore[0]}-${selected.projectedScore[1]} ${selected.away.code}`,
      detail: `${Math.round(selected.confidence * 100)}% confidence / ${Math.round(selected.volatility)} volatility`,
      href: "#signal",
    },
    {
      key: "02",
      label: "Player Layer",
      value: topPlayer.player.displayName,
      detail: `${topPlayer.score.toFixed(2)} live rating / ${topPlayer.grade} grade`,
      href: "#players",
    },
    {
      key: "03",
      label: "Showreel",
      value: "Teaser video",
      detail: "No-country World Cup data cockpit motion",
      href: "#teaser",
    },
    {
      key: "04",
      label: "Injective Rail",
      value: "x402 / CCTP / MCP / Skill",
      detail: "Paid scout intel, fan pool memo, agent-readable match tools",
      href: "#injective",
    },
  ];

  return (
    <section className="studio-index" aria-label="Cup Signal studio index">
      <div className="shell studio-index-inner">
        <div className="studio-index-copy">
          <p className="eyebrow">Case Index</p>
          <h2>A football signal system staged as a live product film.</h2>
        </div>
        <div className="studio-index-list">
          {rows.map((row) => (
            <a href={row.href} key={row.key}>
              <span>{row.key}</span>
              <strong>{row.label}</strong>
              <b>{row.value}</b>
              <em>{row.detail}</em>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function TeaserVideoPanel() {
  return (
    <section id="teaser" className="teaser-video-panel reveal-block" aria-label="Cup Signal motion background">
      <video
        className="teaser-video-bg"
        src={assetPath("/media/cup-signal-loop.mp4")}
        poster={assetPath("/media/cup-signal-teaser-cover.jpg")}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        aria-hidden="true"
      />
      <div className="teaser-video-shade" aria-hidden="true" />
      <div className="shell teaser-video-stage">
        <div className="teaser-video-copy">
          <p className="eyebrow">Matchday Film</p>
          <h2>World Cup data cockpit in motion</h2>
          <p>
            A looping matchday signal layer carries pressure maps, paid scout intel, and fan-pool settlement rails through
            the Cup Signal case narrative.
          </p>
        </div>
        <div className="teaser-signal-stack" aria-label="Motion layer signals">
          <span>SHOWREEL / 00:06 LOOP</span>
          <strong>x402 / CCTP / MCP / AGENT SKILL</strong>
          <b>FOOTBALL DATA STUDIO</b>
        </div>
      </div>
      <div className="shell teaser-video-rail" aria-hidden="true">
        <span>01 Live pressure map</span>
        <span>02 Paid scout brief</span>
        <span>03 Fan pool settlement memo</span>
      </div>
    </section>
  );
}

function LanguageToggle({ lang, onToggle }: { lang: Lang; onToggle: () => void }) {
  return (
    <button className="language-toggle" onClick={onToggle} aria-label={copy[lang].langLabel}>
      <span>{lang.toUpperCase()}</span>
      <strong>{copy[lang].langButton}</strong>
    </button>
  );
}

function InjectivePlaybookPanel({
  lang,
  onchainProof,
  x402Payment,
  cctpPayment,
}: {
  lang: Lang;
  onchainProof: OnchainProof | null;
  x402Payment: X402PaymentProof | null;
  cctpPayment: CctpPaymentProof | null;
}) {
  return (
    <section className="shell injective-playbook reveal-block tilt-card" aria-label="Injective technology playbook">
      <div className="playbook-copy">
        <p className="eyebrow">Injective Playbook</p>
        <h2>{copy[lang].playbookTitle}</h2>
        <p>{copy[lang].playbookBody}</p>
      </div>
      <div className="playbook-score">
        <strong>{injectivePlaybookSummary.totalTechHooks}/4</strong>
        <span>technical hooks covered</span>
      </div>
      <div className={onchainProof?.status === "live" ? "onchain-proof live" : "onchain-proof pending"}>
        <span>{onchainProof?.status === "live" ? "ON-CHAIN PROOF LIVE" : "TESTNET DEPLOYMENT PENDING"}</span>
        {onchainProof?.status === "live" && onchainProof.contract ? (
          <a href={onchainProof.contract.explorer} target="_blank" rel="noreferrer">
            {onchainProof.contract.address}
          </a>
        ) : (
          <code>Injective EVM Testnet / eip155:1439</code>
        )}
      </div>
      <div className={x402Payment?.status === "settled" ? "onchain-proof live" : "onchain-proof pending"}>
        <span>{x402Payment ? "REAL x402 SETTLEMENT LIVE" : "x402 SETTLEMENT PROOF PENDING"}</span>
        {x402Payment ? (
          <a href={x402Payment.transaction.explorer} target="_blank" rel="noreferrer">
            {x402Payment.payment.amountUsdc} USDC / {x402Payment.transaction.hash}
          </a>
        ) : (
          <code>Injective native USDC / EIP-3009</code>
        )}
      </div>
      <div className={cctpPayment?.status === "success" ? "onchain-proof live" : "onchain-proof pending"}>
        <span>{cctpPayment ? "CCTP V2 TRANSFER LIVE" : "CCTP TRANSFER PROOF PENDING"}</span>
        {cctpPayment ? (
          <a href={cctpPayment.explorer.destination} target="_blank" rel="noreferrer">
            {cctpPayment.amount} USDC / {cctpPayment.balanceBefore} → {cctpPayment.balanceAfter}
          </a>
        ) : (
          <code>Base Sepolia → Injective Testnet</code>
        )}
      </div>
      <div className="playbook-grid">
        {injectivePlays.map((play) => (
          <PlaybookCard key={play.key} play={play} />
        ))}
      </div>
    </section>
  );
}

function PlaybookCard({ play }: { play: InjectivePlay }) {
  const icon =
    play.key === "x402" ? (
      <CircleDollarSign size={18} />
    ) : play.key === "cctp" ? (
      <GitBranch size={18} />
    ) : play.key === "mcp" ? (
      <FileJson size={18} />
    ) : (
      <Bot size={18} />
    );
  return (
    <article className={`playbook-card ${play.key}`}>
      <div className="playbook-card-head">
        {icon}
        <span>{play.status}</span>
      </div>
      <h3>{play.label}</h3>
      <p>{play.hook}</p>
      <div className="playbook-lines">
        <span>
          <strong>Fan action</strong>
          {play.fanAction}
        </span>
        <span>
          <strong>Proof</strong>
          {play.proof}
        </span>
        <span>
          <strong>Bonus</strong>
          {play.scoreBoost}
        </span>
      </div>
    </article>
  );
}

function RealWorldCupDataPanel({
  lang,
  data,
  refreshState,
  refreshMessage,
  onRefresh,
}: {
  lang: Lang;
  data: WorldCupRuntimeData;
  refreshState: RefreshState;
  refreshMessage: string;
  onRefresh: () => void;
}) {
  const openingMatches = data.matches.slice(0, 6);
  const knockoutPreview = data.matches.filter((match) => match.type !== "group").slice(0, 6);
  const lookup = new Map(data.teams.map((team) => [team.fifaCode, team]));
  const stats = {
    teams: data.teams.length,
    groups: data.groups.length,
    matches: data.matches.length,
    stadiums: data.stadiums.length,
  };
  return (
    <section id="real-data" className="shell real-data-panel reveal-block tilt-card" aria-label="World Cup real data panel">
      <div className="real-data-head">
        <div>
          <p className="eyebrow">Real Data Layer</p>
          <h2>{copy[lang].realTitle}</h2>
          <p>{copy[lang].realBody}</p>
        </div>
        <div className="source-stack">
          <span>{data.source.teams}</span>
          <span>{data.mode === "live-browser" ? `Browser refresh ${data.updatedAt}` : data.source.knockout}</span>
          <button className={`live-refresh-button ${refreshState}`} onClick={onRefresh} disabled={refreshState === "loading"}>
            <Activity size={15} />
            {refreshState === "loading" ? copy[lang].refreshLoading : copy[lang].refreshIdle}
          </button>
        </div>
      </div>
      {refreshMessage ? <div className={`refresh-status ${refreshState}`}>{refreshMessage}</div> : null}
      <div className="real-stat-grid">
        <Metric label="Teams" value={String(stats.teams)} />
        <Metric label="Groups" value={String(stats.groups)} />
        <Metric label="Matches" value={String(stats.matches)} />
        <Metric label="Stadiums" value={String(stats.stadiums)} />
      </div>
      <div className="real-data-body">
        <div className="group-matrix">
          {data.groups.map((group) => (
            <div key={group.group}>
              <strong>Group {group.group}</strong>
              {group.teams.map((code, index) => {
                const team = lookup.get(code);
                return (
                  <span key={`${group.group}-${code}-${index}`}>
                    {team?.flag ? <img src={team.flag} alt="" /> : <i />}
                    {team?.name ?? code}
                  </span>
                );
              })}
            </div>
          ))}
        </div>
        <div className="schedule-stack">
          <div>
            <strong>Opening slate</strong>
            {openingMatches.map((match) => (
              <MatchMini key={match.id} match={match} />
            ))}
          </div>
          <div>
            <strong>Knockout slots</strong>
            {knockoutPreview.map((match) => (
              <MatchMini key={match.id} match={match} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function MatchMini({ match }: { match: WorldCupMatch }) {
  return (
    <article className="match-mini">
      <span>#{match.id}</span>
      <strong>
        {match.homeName} / {match.awayName}
      </strong>
      <small>
        {match.localDate} · {match.city || match.stadium}
      </small>
    </article>
  );
}

function LiveTicker({ selected, topPlayer }: { selected: Prediction; topPlayer: PlayerScore }) {
  const items = [
    `${selected.home.code} ${selected.projectedScore[0]}-${selected.projectedScore[1]} ${selected.away.code}`,
    `xG ${selected.match.xgHome.toFixed(2)} / ${selected.match.xgAway.toFixed(2)}`,
    `Top player ${topPlayer.player.displayName} ${topPlayer.score.toFixed(2)}`,
    `Volatility ${Math.round(selected.volatility)}`,
    `x402 resource /api/premium-report/${selected.match.id}`,
    "MCP tool rank_match_players ready",
  ];
  return (
    <section className="ticker-band" aria-label="Live match data ticker">
      <div className="ticker-track">
        {[...items, ...items].map((item, index) => (
          <span key={`${item}-${index}`}>{item}</span>
        ))}
      </div>
    </section>
  );
}

function PlayerDashboard({
  lang,
  scores,
  selectedScore,
  selectedPlayerId,
  onSelect,
  ratingMode,
  onMode,
  windowKey,
  onWindow,
}: {
  lang: Lang;
  scores: PlayerScore[];
  selectedScore: PlayerScore;
  selectedPlayerId: string;
  onSelect: (id: string) => void;
  ratingMode: RatingMode;
  onMode: (mode: RatingMode) => void;
  windowKey: WindowKey;
  onWindow: (key: WindowKey) => void;
}) {
  const top = scores[0];
  const teamSplit = scores.reduce(
    (acc, score) => {
      acc[score.player.side] += score.score;
      return acc;
    },
    { home: 0, away: 0 },
  );
  const homeScores = scores.filter((score) => score.player.side === "home").length;
  const awayScores = scores.filter((score) => score.player.side === "away").length;
  const homeAvg = teamSplit.home / homeScores;
  const awayAvg = teamSplit.away / awayScores;
  const homeTeam = scores.find((score) => score.player.side === "home")?.player;
  const awayTeam = scores.find((score) => score.player.side === "away")?.player;

  return (
    <section id="players" className="shell player-dashboard reveal-block" aria-label="Player data dashboard">
      <div className="player-head">
        <div>
          <p className="eyebrow">Player Data Board</p>
          <h2>{copy[lang].playerTitle}</h2>
          <p>{copy[lang].playerBody}</p>
          <p className="data-disclaimer">Official schedule drives the match context. Player ratings are a product analytics layer for the matchday workflow.</p>
        </div>
        <div className="mode-controls" aria-label="Player dashboard controls">
          <div>
            {(["balanced", "attack", "defense", "pressing"] as RatingMode[]).map((mode) => (
              <button key={mode} className={ratingMode === mode ? "active" : ""} onClick={() => onMode(mode)}>
                {mode}
              </button>
            ))}
          </div>
          <div>
            {(["live", "last5", "season"] as WindowKey[]).map((key) => (
              <button key={key} className={windowKey === key ? "active" : ""} onClick={() => onWindow(key)}>
                {key}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="player-grid">
        <section className="panel player-list-panel tilt-card">
          <div className="panel-title">
            <Medal size={18} />
            <span>Live Rating Table</span>
          </div>
          <div className="team-edge">
            <div>
              <img src={assetPath(homeTeam?.crest ?? "/teams/crest-mex.svg")} alt="" />
              <span>{homeTeam?.team ?? "Home"} avg</span>
              <strong>{homeAvg.toFixed(2)}</strong>
            </div>
            <div>
              <img src={assetPath(awayTeam?.crest ?? "/teams/crest-eng.svg")} alt="" />
              <span>{awayTeam?.team ?? "Away"} avg</span>
              <strong>{awayAvg.toFixed(2)}</strong>
            </div>
          </div>
          <div className="player-score-list">
            {scores.map((score, index) => (
              <button
                key={score.player.id}
                className={selectedPlayerId === score.player.id ? "player-row active" : "player-row"}
                onClick={() => onSelect(score.player.id)}
              >
                <span className="rank">{String(index + 1).padStart(2, "0")}</span>
                <img src={assetPath(score.player.portrait)} alt="" />
                <span>
                  <strong>
                    {score.player.displayName}
                    <img src={assetPath(score.player.flag)} alt="" />
                    <img src={assetPath(score.player.crest)} alt="" />
                  </strong>
                  <small>
                    {score.player.team} · #{score.player.number} · {score.player.role}
                  </small>
                </span>
                <b>{score.score.toFixed(2)}</b>
              </button>
            ))}
          </div>
        </section>

        <section className="panel player-focus-panel tilt-card">
          <div className="player-identity">
            <img src={assetPath(selectedScore.player.portrait)} alt="" />
            <div>
              <p className="eyebrow">
                <img src={assetPath(selectedScore.player.flag)} alt="" />
                <img src={assetPath(selectedScore.player.crest)} alt="" />#{selectedScore.player.number} · {selectedScore.player.role} ·{" "}
                {selectedScore.player.team}
              </p>
              <h3>{selectedScore.player.name}</h3>
              <div className="trait-strip">
                {selectedScore.player.traits.map((trait) => (
                  <span key={trait}>{trait}</span>
                ))}
              </div>
            </div>
            <div className="grade-badge">
              <strong>{selectedScore.grade}</strong>
              <span>{selectedScore.score.toFixed(2)}</span>
            </div>
          </div>
          <StarCard score={selectedScore} />
          <div className="player-kpis">
            <Metric label="Live impact" value={selectedScore.liveImpact.toFixed(0)} />
            <Metric label="Ability index" value={selectedScore.abilityIndex.toFixed(0)} />
            <Metric label="Form index" value={selectedScore.formIndex.toFixed(0)} />
            <Metric label="Risk" value={selectedScore.risk.toFixed(0)} />
          </div>
          <div className="focus-body">
            <Radar score={selectedScore} />
            <AbilityCompare score={selectedScore} />
          </div>
          <div className="watch-item">
            <Sparkles size={17} />
            <span>{selectedScore.watchItem}</span>
          </div>
        </section>

        <section className="panel player-detail-panel tilt-card">
          <div className="panel-title">
            <LineChart size={18} />
            <span>Events & Form</span>
          </div>
          <Trend values={selectedScore.player.form.trend} />
          <div className="form-note">
            <strong>{selectedScore.player.form.condition}</strong>
            <span>{selectedScore.player.form.note}</span>
          </div>
          <div className="event-list">
            {selectedScore.player.events.map((event) => (
              <div key={`${event.minute}-${event.type}`}>
                <time>{event.minute}'</time>
                <span>
                  <strong>{event.type}</strong>
                  {event.detail}
                </span>
                <b className={event.impact >= 0 ? "positive" : "negative"}>{event.impact > 0 ? "+" : ""}{event.impact.toFixed(2)}</b>
              </div>
            ))}
          </div>
          <StatGrid score={selectedScore} />
        </section>
      </div>
      <div className="player-insight-strip">
        <Gauge size={18} />
        <span>
          Current leader: {top.player.displayName} at {top.score.toFixed(2)}. Difference from normal ability:{" "}
          {selectedScore.delta > 0 ? "+" : ""}
          {selectedScore.delta.toFixed(1)} pts for selected player.
        </span>
      </div>
    </section>
  );
}

function StarCard({ score }: { score: PlayerScore }) {
  const ability = score.player.current;
  const statPairs = [
    ["PAC", ability.pace],
    ["SHO", Math.round((ability.finishing + score.player.live.xg * 10) / 1.1)],
    ["PAS", ability.passing],
    ["DRI", ability.ballCarry],
    ["DEF", ability.defending],
    ["PHY", Math.round((ability.stamina + ability.aerial + ability.pressing) / 3)],
  ];
  const overall = Math.round(Math.min(99, Math.max(55, score.score * 10.6)));
  const tier = overall >= 90 ? "icon" : overall >= 85 ? "toty" : overall >= 75 ? "gold" : "silver";
  return (
    <div className={`star-card ${tier}`} aria-label={`${score.player.displayName} star card`}>
      <div className="star-card-top">
        <strong>{overall}</strong>
        <span>{score.player.role}</span>
        <img src={assetPath(score.player.flag)} alt="" />
        <img src={assetPath(score.player.crest)} alt="" />
      </div>
      <img className="star-card-player" src={assetPath(score.player.portrait)} alt="" />
      <div className="star-card-name">{score.player.displayName.toUpperCase()}</div>
      <div className="star-card-stats">
        {statPairs.map(([label, value]) => (
          <span key={label}>
            <b>{value}</b>
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

function WorldCupMotionPanel({ lang, scores, selected }: { lang: Lang; scores: PlayerScore[]; selected: Prediction }) {
  const leaders = scores.slice(0, 4);
  return (
    <section className="shell motion-panel reveal-block tilt-card" aria-label="World Cup motion layer">
      <div className="motion-copy">
        <p className="eyebrow">Motion Layer</p>
        <h2>{copy[lang].motionTitle}</h2>
        <p>{copy[lang].motionBody}</p>
      </div>
      <div className="motion-stage">
        <img className="route-map" src={assetPath("/worldcup/pitch-routes.svg")} alt="" />
        <img className="motion-trophy" src={assetPath("/worldcup/trophy-line.svg")} alt="" />
        <img className="motion-ball" src={assetPath("/worldcup/football-line.svg")} alt="" />
        <div className="pulse-node node-a" />
        <div className="pulse-node node-b" />
        <div className="pulse-node node-c" />
        <div className="leader-ribbon">
          {leaders.map((score) => (
            <div key={score.player.id}>
              <img src={assetPath(score.player.portrait)} alt="" />
              <img src={assetPath(score.player.flag)} alt="" />
              <span>{score.player.displayName}</span>
              <strong>{score.score.toFixed(2)}</strong>
            </div>
          ))}
        </div>
        <div className="match-chip">
          <span>{selected.match.round}</span>
          <strong>
            {selected.home.code} / {selected.away.code}
          </strong>
        </div>
      </div>
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Radar({ score }: { score: PlayerScore }) {
  const picked = abilityLabels.slice(0, 8);
  const points = picked
    .map((item, index) => {
      const angle = -Math.PI / 2 + (index / picked.length) * Math.PI * 2;
      const radius = 21 + score.player.current[item.key] * 0.47;
      const x = 82 + Math.cos(angle) * radius;
      const y = 82 + Math.sin(angle) * radius;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <div className="radar-card">
      <svg viewBox="0 0 164 164" role="img" aria-label="Current ability radar">
        {[32, 54, 76].map((radius) => (
          <circle key={radius} cx="82" cy="82" r={radius} />
        ))}
        {picked.map((item, index) => {
          const angle = -Math.PI / 2 + (index / picked.length) * Math.PI * 2;
          return (
            <g key={item.key}>
              <line x1="82" y1="82" x2={82 + Math.cos(angle) * 76} y2={82 + Math.sin(angle) * 76} />
              <text x={82 + Math.cos(angle) * 68} y={82 + Math.sin(angle) * 68}>
                {item.short}
              </text>
            </g>
          );
        })}
        <polygon points={points} />
      </svg>
      <div>
        <strong>{score.strengths.join(" / ")}</strong>
        <span>Top live strengths under selected mode</span>
      </div>
    </div>
  );
}

function AbilityCompare({ score }: { score: PlayerScore }) {
  return (
    <div className="ability-compare">
      {abilityLabels.map((item) => {
        const current = score.player.current[item.key];
        const base = score.player.baseline[item.key];
        const delta = current - base;
        return (
          <div key={item.key} className="ability-line">
            <span>{item.label}</span>
            <div className="dual-track">
              <i className="base" style={{ width: `${base}%` }} />
              <i className={delta >= 0 ? "current up" : "current down"} style={{ width: `${current}%` }} />
            </div>
            <b className={delta >= 0 ? "positive" : "negative"}>
              {delta > 0 ? "+" : ""}
              {delta}
            </b>
          </div>
        );
      })}
    </div>
  );
}

function Trend({ values }: { values: number[] }) {
  const max = Math.max(...values);
  const min = Math.min(...values);
  const points = values
    .map((value, index) => {
      const x = 10 + index * (180 / (values.length - 1));
      const y = 76 - ((value - min) / Math.max(0.1, max - min)) * 56;
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <svg className="trend-line" viewBox="0 0 200 90" aria-label="recent rating trend">
      <polyline points={points} />
      {values.map((value, index) => (
        <circle
          key={`${value}-${index}`}
          cx={10 + index * (180 / (values.length - 1))}
          cy={76 - ((value - min) / Math.max(0.1, max - min)) * 56}
          r="3.5"
        />
      ))}
    </svg>
  );
}

function StatGrid({ score }: { score: PlayerScore }) {
  const stats = score.player.live;
  const rows = [
    ["xG", stats.xg.toFixed(2)],
    ["xA", stats.xa.toFixed(2)],
    ["Shots", `${stats.shots}/${stats.shotsOnTarget}`],
    ["Key passes", stats.keyPasses],
    ["Prog passes", stats.progressivePasses],
    ["Carries", stats.progressiveCarries],
    ["Duels won", stats.duelsWon],
    ["Recoveries", stats.ballRecoveries],
    ["Pressures", stats.pressures],
    ["Distance", `${stats.distanceKm.toFixed(1)}km`],
  ];
  return (
    <div className="stat-grid">
      {rows.map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}

function Hero({
  selected,
  lang,
}: {
  selected: Prediction;
  lang: Lang;
}) {
  const navLinks = [
    { href: "#signal", label: "Signal", compact: false },
    { href: "#players", label: "Players", compact: false },
    { href: "#injective", label: "Flow", compact: false },
    { href: "#real-data", label: "Data", compact: false },
    { href: "https://github.com/Chengyuann/cup-signal-injective/blob/main/docs/JUDGE_GUIDE.md", label: "Judge Guide", external: true },
    { href: assetPath("/proofs/judge-proof.json"), label: "Proof", external: true },
  ];

  return (
    <header className="hero">
      <div className="spotlight-layer" aria-hidden="true" />
      <nav className="topbar shell" aria-label="Project navigation">
        {navLinks.map((item) => (
          <a
            href={item.href}
            key={item.href}
            className={item.compact === false ? "desktop-nav-link" : undefined}
            target={item.external ? "_blank" : undefined}
            rel={item.external ? "noreferrer" : undefined}
          >
            {item.label}
          </a>
        ))}
      </nav>
      <div className="hero-heading-wrap reveal-block">
        <h1 className="hero-heading">Cup Signal</h1>
      </div>
      <div className="hero-showpiece reveal-block" aria-label="Cup Signal motion preview">
        <video
          src={assetPath("/media/cup-signal-loop.mp4")}
          poster={assetPath("/media/cup-signal-teaser-cover.jpg")}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          aria-hidden="true"
        />
        <div className="hero-score-chip">
          <span>{selected.match.round}</span>
          <strong>
            {selected.home.code} {selected.projectedScore[0]} - {selected.projectedScore[1]} {selected.away.code}
          </strong>
        </div>
      </div>
      <div className="shell hero-bottom reveal-block">
        <p>
          {lang === "zh"
            ? "把世界杯现场噪音压缩成一个可交易、可展示、可交给 Agent 的信号。"
            : "A matchday AI cockpit that turns World Cup chaos into one tradable signal."}
        </p>
        <a className="hero-contact-pill" href="#signal">
          Open Signal
        </a>
      </div>
    </header>
  );
}

function MatchRail({
  predictions,
  selectedId,
  onSelect,
}: {
  predictions: Prediction[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <section className="panel match-rail tilt-card" aria-label="Match list">
      <div className="panel-title">
        <Radio size={18} />
        <span>Fixtures</span>
      </div>
      <div className="match-list">
        {predictions.map((prediction) => (
          <button
            key={prediction.match.id}
            className={prediction.match.id === selectedId ? "match-item active" : "match-item"}
            onClick={() => onSelect(prediction.match.id)}
          >
            <span>{prediction.match.round}</span>
            <strong>
              {prediction.home.code} / {prediction.away.code}
            </strong>
            <small>
              {prediction.match.status === "live"
                ? `${prediction.match.minute}' live`
                : prediction.match.kickoffLocal.split(" ").slice(0, 2).join(" ")}
            </small>
          </button>
        ))}
      </div>
    </section>
  );
}

function SignalPanel({ selected }: { selected: Prediction }) {
  const bars = [
    { label: selected.home.name, value: selected.homeWin, className: "home" },
    { label: "Draw", value: selected.draw, className: "draw" },
    { label: selected.away.name, value: selected.awayWin, className: "away" },
  ];

  return (
    <section id="signal" className="panel signal-panel tilt-card" aria-label="AI signal panel">
      <div className="panel-title">
        <BarChart3 size={18} />
        <span>AI Match Signal</span>
      </div>
      <div className="signal-head">
        <div>
          <p>{selected.match.venue}</p>
          <h2>
            {selected.home.name} vs {selected.away.name}
          </h2>
        </div>
        <div className="confidence">
          <span>{Math.round(selected.confidence * 100)}%</span>
          <small>confidence</small>
        </div>
      </div>
      <div className="prob-bars">
        {bars.map((bar) => (
          <div className="bar-row" key={bar.label}>
            <span>{bar.label}</span>
            <div className="bar-track">
              <i className={bar.className} style={{ width: `${Math.round(bar.value * 100)}%` }} />
            </div>
            <strong>{formatPercent(bar.value)}</strong>
          </div>
        ))}
      </div>
      <div className="readout">
        <div>
          <span>{selected.match.scoreLabel === "final" ? "Final score" : "Projected score"}</span>
          <strong>
            {selected.projectedScore[0]} - {selected.projectedScore[1]}
          </strong>
        </div>
        <div>
          <span>Volatility</span>
          <strong>{Math.round(selected.volatility)}</strong>
        </div>
        <div>
          <span>xG</span>
          <strong>
            {selected.match.xgHome.toFixed(2)} / {selected.match.xgAway.toFixed(2)}
          </strong>
        </div>
      </div>
      <div className="analysis-list">
        {selected.tacticalRead.map((line) => (
          <p key={line}>
            <CheckCircle2 size={16} />
            {line}
          </p>
        ))}
      </div>
      <div className="action-note">
        <ShieldCheck size={18} />
        <span>{selected.recommendedAction}</span>
      </div>
    </section>
  );
}

function ControlPanel({
  weights,
  setWeights,
}: {
  weights: Weights;
  setWeights: React.Dispatch<React.SetStateAction<Weights>>;
}) {
  return (
    <section className="panel controls tilt-card" aria-label="Model controls">
      <div className="panel-title">
        <SlidersHorizontal size={18} />
        <span>Model Weights</span>
      </div>
      <p className="muted">
        Tune the agent before kickoff. Hosts can make it favor live form, defensive structure, or local travel comfort.
      </p>
      <div className="sliders">
        {(Object.keys(weights) as WeightKey[]).map((key) => (
          <label key={key}>
            <span>
              {weightLabels[key]}
              <strong>{weights[key]}</strong>
            </span>
            <input
              type="range"
              min="0"
              max="60"
              value={weights[key]}
              onChange={(event) =>
                setWeights((current) => ({
                  ...current,
                  [key]: Number(event.target.value),
                }))
              }
            />
          </label>
        ))}
      </div>
      <button className="reset-button" onClick={() => setWeights(defaultWeights)}>
        Reset Scout Model
      </button>
    </section>
  );
}

function InjectivePanel({
  paid,
  loading,
  onUnlock,
  brief,
}: {
  paid: boolean;
  loading: boolean;
  onUnlock: () => void;
  brief: ReturnType<typeof buildWatchBrief>;
}) {
  return (
    <section id="injective" className="panel injective-panel tilt-card" aria-label="Injective integration panel">
      <div className="panel-title">
        <CircleDollarSign size={18} />
        <span>Injective Flow</span>
      </div>
      <div className="flow-grid">
        <FlowStep icon={<WalletCards size={20} />} title="x402 paid report" text={`${brief.payment.price} premium report gate`} />
        <FlowStep icon={<GitBranch size={20} />} title="USDC CCTP transfer" text={`${brief.cctp.source} to ${brief.cctp.destination}`} />
        <FlowStep icon={<FileJson size={20} />} title="MCP tool" text="forecast_match and build_watch_brief" />
        <FlowStep icon={<Bot size={20} />} title="Agent Skill" text="watch-party analyst handoff" />
      </div>
      <div className="paywall">
        <div>
          <span className="eyebrow">Premium x402 Resource</span>
          <h3>{brief.headline}</h3>
          <p>{brief.freeSummary}</p>
        </div>
        {!paid ? (
          <button onClick={onUnlock} disabled={loading}>
            {loading ? "Checking payment header" : "Preview paid report"}
          </button>
        ) : (
          <span className="paid-badge">Unlocked</span>
        )}
      </div>
      {paid ? (
        <div className="premium-report">
          {brief.premiumReport.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      ) : (
        <div className="locked-report">
          <div />
          <div />
          <div />
        </div>
      )}
    </section>
  );
}

function FlowStep({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="flow-step">
      {icon}
      <strong>{title}</strong>
      <span>{text}</span>
    </div>
  );
}

function AgentPanel({ selected }: { selected: Prediction }) {
  return (
    <section className="panel agent-panel tilt-card" aria-label="Agent skills panel">
      <div className="panel-title">
        <Bot size={18} />
        <span>Agent Output</span>
      </div>
      <div className="agent-terminal">
        <p>
          <span>tool</span> forecast_match --match {selected.match.id}
        </p>
        <p>
          <span>result</span> {selected.home.code} {formatPercent(selected.homeWin)} / draw {formatPercent(selected.draw)} /{" "}
          {selected.away.code} {formatPercent(selected.awayWin)}
        </p>
        <p>
          <span>skill</span> generate_watch_party_brief
        </p>
        <p>
          <span>next</span> publish screenshot in X reply during live match for Global Cup score.
        </p>
      </div>
      <div className="fan-actions">
        {selected.fanUtility.map((item) => (
          <div key={item}>
            <Trophy size={16} />
            <span>{item}</span>
          </div>
        ))}
      </div>
      <div className="status-strip">
        <Activity size={16} />
        <span>Local model, deterministic output, no hidden API key required for demo.</span>
      </div>
    </section>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
